import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($token: String!, $password: String!) {
    resetPassword(token: $token, password: $password) {
      user {
        id
      }
      authToken
      errors
    }
  }
`;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { setToken } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: (vars: { token: string; password: string }) =>
      graphqlClient.request<{
        resetPassword: {
          user: { id: string } | null;
          authToken: string | null;
          errors: string[];
        };
      }>(RESET_PASSWORD_MUTATION, vars),
    onSuccess: async (data) => {
      if (data.resetPassword.user && data.resetPassword.authToken) {
        await setToken(data.resetPassword.authToken);
        navigate("/");
      } else {
        setErrors(data.resetPassword.errors);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    if (password !== confirmPassword) {
      setErrors(["Slaptažodžiai nesutampa"]);
      return;
    }

    mutation.mutate({ token, password });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/icon.svg" alt="Boardo" className="h-12 w-12" />
            <span className="text-2xl font-bold text-primary-800">Boardo</span>
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            Naujas slaptažodis
          </h1>
        </div>

        <div className="card p-8">
          {errors.length > 0 && (
            <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Naujas slaptažodis
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Pakartokite slaptažodį
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                required
                minLength={6}
              />
            </div>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn-primary w-full"
            >
              {mutation.isPending
                ? "Keičiamas..."
                : "Pakeisti slaptažodį"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary-800 hover:text-primary-700"
            >
              Grįžti į prisijungimą
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
